import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Search, Target, TrendingUp, Phone, CheckCircle, Star } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GoogleAds = () => {

  return (
    <>
      <SEO 
        title="Google Ads for Lawn Care & Landscaping | Get More Clients"
        description="Google Ads management for lawn care & landscaping companies. Turn clicks into paying clients with conversion-optimized campaigns and proven ROI."
        keywords="google ads lawn care, google ads landscaping, lawn care PPC, landscaping paid advertising"
        canonical="https://www.leafrushmarketing.com/growth-add-ons/google-ads/"
        ogUrl="https://www.leafrushmarketing.com/growth-add-ons/google-ads/"
      />
      <div className="flex flex-col">
      {/* Hero */}
      <section className="py-20 md:py-32">
        <div className="container px-4 md:px-6">
          <div className="mx-auto max-w-4xl text-center space-y-8">
            <Badge variant="secondary" className="mb-4">
              Proven Google Ads for Lawn Care & Landscaping
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              Google Ads for <span className="text-primary">Lawn Care & Landscaping</span> Companies
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Targeted ads that reach homeowners actively searching for your services. 
              We build conversion-optimized funnels proven to turn clicks into paying customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/contact#get-started">
                <Button size="lg">Get Started</Button>
              </NavLink>
              <NavLink to="/websites">
                <Button size="lg" variant="outline">View Our Work</Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Generation Process */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Lead Generation Process</h2>
            <p className="text-xl text-muted-foreground">
              Four proven phases to build your growth engine
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">
                  1
                </div>
                <CardTitle className="text-xl">Dig Deep & Map the Field</CardTitle>
                <CardDescription>Research Phase</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  We analyze your service area, competition, and target customers to identify the most profitable keywords and ad strategies.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">
                  2
                </div>
                <CardTitle className="text-xl">Build Your Growth Engine</CardTitle>
                <CardDescription>Build Phase</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Create conversion-optimized landing pages, write compelling ad copy, and set up tracking to measure every lead and call.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">
                  3
                </div>
                <CardTitle className="text-xl">Launch, Watch, Tweak</CardTitle>
                <CardDescription>Growth Phase</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Launch campaigns, monitor performance daily, and continuously optimize to improve cost-per-lead and conversion rates.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">
                  4
                </div>
                <CardTitle className="text-xl">Scale What Works</CardTitle>
                <CardDescription>Expansion Phase</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Double down on winning campaigns, expand to new service areas, and maximize your return on ad spend.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Website First: Your Landing Page Foundation */}
      <section className="py-12 bg-primary/5">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Website First: Your Landing Page Foundation</h2>
            <p className="text-muted-foreground mb-4">
              Your landscaping website design is the conversion layer for your ads. We don't send expensive ad traffic to a generic homepage—we build custom landing pages on your website that match each ad's intent and maximize conversions.
            </p>
            <p className="text-muted-foreground mb-6">
              That's why we recommend having a professional lawn care website design or landscaping website design in place before starting Google Ads. Your website is where clicks become customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/lawn-care-website-design/">
                <Button variant="outline">Lawn Care Website Design</Button>
              </NavLink>
              <NavLink to="/landscaping-website-design/">
                <Button variant="outline">Landscaping Website Design</Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Ads Work */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Our Ads Work</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Most agencies treat lawn care like any other business. We don't.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2">
              <CardHeader>
                <Search className="h-10 w-10 text-primary mb-4" />
                <CardTitle>Hyper-Targeted Ads</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  We target homeowners searching for specific services in your exact service area. No wasted clicks on tire-kickers or out-of-area leads.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Location-specific targeting</li>
                  <li>• Service-based keywords</li>
                  <li>• Seasonal campaign adjustments</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <Target className="h-10 w-10 text-primary mb-4" />
                <CardTitle>Conversion-Optimized Landing Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  We don't send traffic to generic pages. Each ad goes to a custom landing page designed to convert that specific service search.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Service-specific pages</li>
                  <li>• Clear calls-to-action</li>
                  <li>• Trust signals and testimonials</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <Phone className="h-10 w-10 text-primary mb-4" />
                <CardTitle>Call & Lead Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Know exactly which ads, keywords, and campaigns drive actual phone calls and form submissions. No guessing.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Call tracking numbers</li>
                  <li>• Form submission tracking</li>
                  <li>• ROI reporting</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <TrendingUp className="h-10 w-10 text-primary mb-4" />
                <CardTitle>Results-First Focus</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  We optimize for leads and revenue, not vanity metrics. Your success is measured in booked jobs, not impressions.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Cost-per-lead optimization</li>
                  <li>• Continuous A/B testing</li>
                  <li>• Monthly performance reviews</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section id="case-studies" className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Real Results from Real Clients</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="text-2xl">Jason Yard Scaping</CardTitle>
                <CardDescription>Residential Landscaping Services</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground">
                  Within 6 months of launching Google Ads, Jason Yard Scaping went from struggling to find customers to having a waitlist for premium landscaping projects.
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-accent/50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">249</p>
                    <p className="text-sm text-muted-foreground">Leads</p>
                  </div>
                  <div className="text-center p-4 bg-accent/50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">$27</p>
                    <p className="text-sm text-muted-foreground">Cost per Lead</p>
                  </div>
                  <div className="text-center p-4 bg-accent/50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">14.3x</p>
                    <p className="text-sm text-muted-foreground">ROI</p>
                  </div>
                </div>

                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                  "The targeted ads brought us exactly the customers we wanted—homeowners ready to invest in quality landscaping."
                </blockquote>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="text-2xl">Pond Hill Greenscapes</CardTitle>
                <CardDescription>Commercial & Residential Lawn Care</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground">
                  Pond Hill expanded into three new service areas in one season thanks to scalable Google Ads campaigns that consistently delivered qualified leads.
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-accent/50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">89</p>
                    <p className="text-sm text-muted-foreground">Leads</p>
                  </div>
                  <div className="text-center p-4 bg-accent/50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">$42</p>
                    <p className="text-sm text-muted-foreground">Cost per Lead</p>
                  </div>
                  <div className="text-center p-4 bg-accent/50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">7.6x</p>
                    <p className="text-sm text-muted-foreground">ROI</p>
                  </div>
                </div>

                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                  "Best marketing decision we ever made. The campaigns paid for themselves in the first month."
                </blockquote>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Clients Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card>
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "We tried other agencies before. Leaf Rush is the only one that actually understands lawn care marketing."
                </p>
                <p className="font-semibold">— Tom Rodriguez</p>
                <p className="text-sm text-muted-foreground">Rodriguez Lawn Services</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "The call tracking shows us exactly what's working. We're spending smarter and growing faster."
                </p>
                <p className="font-semibold">— Jennifer Adams</p>
                <p className="text-sm text-muted-foreground">Green Earth Landscaping</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "Best ROI we've ever seen from marketing. The leads are qualified and ready to book."
                </p>
                <p className="font-semibold">— Marcus Thompson</p>
                <p className="text-sm text-muted-foreground">Thompson Lawn Care</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="max-w-3xl mx-auto">
            <AccordionItem value="item-1">
              <AccordionTrigger>How much should I budget for Google Ads?</AccordionTrigger>
              <AccordionContent>
                We recommend starting with at least $1,500-$3,000/month in ad spend for most markets. This gives us enough data to optimize and typically generates 30-75 qualified leads per month depending on your service area and competition.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>How quickly will I see results?</AccordionTrigger>
              <AccordionContent>
                Most clients start receiving leads within the first week. However, it takes 2-3 months to fully optimize campaigns and achieve the best cost-per-lead. The longer we run, the better the results get.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>Do I need a website before starting Google Ads?</AccordionTrigger>
              <AccordionContent>
                Yes, you need a professional, mobile-friendly website. If you don't have one yet, start with our website packages first. We can then build custom landing pages for your Google Ads campaigns.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger>What's your management fee?</AccordionTrigger>
              <AccordionContent>
                Our management fee is 15-20% of ad spend depending on budget size, with a minimum of $500/month. This includes campaign setup, ongoing optimization, landing page creation, tracking setup, and monthly reporting.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>Can I pause campaigns during the off-season?</AccordionTrigger>
              <AccordionContent>
                Absolutely. Many lawn care businesses scale back or pause campaigns in winter and ramp up in spring. We can adjust your budget seasonally to match your business needs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger>How do you track ROI?</AccordionTrigger>
              <AccordionContent>
                We set up call tracking and form tracking so you know exactly which ads generate leads. You'll get monthly reports showing leads, cost-per-lead, and ROI. Many clients also share booking/revenue data so we can optimize for actual business growth.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Calendly Scheduling */}
      <CalendlyEmbed />
      </div>
    </>
  );
};

export default GoogleAds;
